import { AxiosRequest } from "_services/api";
import {
  IData,
  IHttpError,
  IHttpResponse,
} from "_services/api/interface/HttpResponse";
import { usePaginationSortSearch } from "containers/Table/hooks/usePaginationSortSearch";
import { useCallback, useState } from "react";
import { HttpResponse } from "_services/api/HttpResponse";
import { HttpError } from "_services/api/HttpError";
import { IFiltroBloqueioAdministrativo } from "../interfaces/IFiltroBloqueioAdministrativo";
import { IListarLotesBloqueio } from "../interfaces/IListarLotesBloqueio";
import { Data } from "_utils/Data";
import { IListarDadosLote } from "../interfaces/IListarDadosLote";
import { IResumoLote } from "../interfaces/IResumoLote";
import { defaultResumoLote } from "../constants/defaultResumoLote";
import { IDadosCriarLote } from "../interfaces/IDadosCriarLote";
import { IPlanilhaCriarLote } from "../interfaces/IPlanilhaCriarLote";
import { IListarItensBloqueio } from "../interfaces/IListarItensBloqueio";
import { IFiltroItensBloqueioAdministrativo } from "../interfaces/IFiltroItensBloqueioAdministrativo";
import { IDadosHistoricoVeiculo } from "../interfaces/IDadosHistoricoVeiculo";
import { limparCnpj } from "_utils/validarCpfCnpj";

const LIMITE_EXPORTACAO = 10000;

export function useBloqueioAdministrativo() {
  const msAw = AxiosRequest.makeApi(process.env.REACT_APP_MS_AW);
  const api = AxiosRequest.makeApi();
  const { totalRows, setTotalRows } = usePaginationSortSearch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<IHttpResponse<IData | any>>();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(50);
  const [totalRowsDetalhes, setTotalRowsDetalhes] = useState<number>(0);
  const [loadingDetalhes, setLoadingDetalhes] = useState(false);
  const [pageDetalhes, setPageDetalhes] = useState<number>(1);
  const [limitDetalhes, setLimitDetalhes] = useState<number>(50);
  const [filtro, setFiltro] = useState<IFiltroBloqueioAdministrativo>({});
  const [dadosLotesBloqueio, setDadosLotesBloqueio] = useState<
    IListarLotesBloqueio[]
  >([]);
  const [dadosLote, setDadosLote] = useState<IListarDadosLote[]>([]);
  const [resumoLote, setResumoLote] = useState<IResumoLote>(defaultResumoLote);
  const [pesquisaItensLote, setPesquisaItenslote] = useState<string>("");
  const [listaItensBloqueio, setListaItensBloqueio] = useState<
    IListarItensBloqueio[]
  >([]);
  const [loadingItens, setLoadingItens] = useState(false);
  const [pageItens, setPageItens] = useState<number>(1);
  const [limitItens, setLimitItens] = useState<number>(50);
  const [filtroItens, setFiltroItens] =
    useState<IFiltroItensBloqueioAdministrativo>({});
  const [totalRowsItens, setTotalRowsItens] = useState<number>(0);
  const [itensSelecionados, setItensSelecionados] = useState<
    IListarItensBloqueio[]
  >([]);
  const [clearRowsListaItens, setClearRowsListaItens] =
    useState<boolean>(false);
  const [historicoVeiculo, setHistoricoVeiculo] = useState<
    IDadosHistoricoVeiculo[]
  >([]);

  const getParamsFiltroItens = (
    obj: any,
    dadosFiltros: IFiltroItensBloqueioAdministrativo,
  ) => {
    if (dadosFiltros.idEmpresa) {
      obj.idEmpresa = dadosFiltros.idEmpresa;
    }

    if (dadosFiltros.idsLotes) {
      obj.lotes = JSON.stringify(dadosFiltros.idsLotes);
    }

    if (dadosFiltros.idsProcessos) {
      obj.processos = JSON.stringify(dadosFiltros.idsProcessos);
    }

    if (dadosFiltros.dadosVeiculos) {
      obj.veiculos = JSON.stringify(dadosFiltros.dadosVeiculos);
    }

    if (dadosFiltros.cnpj) {
      obj.cnpj = limparCnpj(dadosFiltros.cnpj);
    }

    if (dadosFiltros.statusSolicitacao) {
      obj.status = dadosFiltros.statusSolicitacao;
    }

    if (dadosFiltros.situacao) {
      obj.situacao = dadosFiltros.situacao;
    }

    if (
      dadosFiltros.dataCriacaoLote &&
      dadosFiltros.dataCriacaoLote[0] &&
      dadosFiltros.dataCriacaoLote[1]
    ) {
      obj.dataInicio = new Data(dadosFiltros.dataCriacaoLote[0]).getDataEua();
      obj.dataFim = new Data(dadosFiltros.dataCriacaoLote[1]).getDataEua();
    }

    const params = Object.keys(obj)
      .map(
        (key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`,
      )
      .join("&");

    return params;
  };

  const listarItens = useCallback(
    async (dadosFiltros?: IFiltroItensBloqueioAdministrativo) => {
      setLoadingItens(true);
      const obj: any = { pagina: pageItens, limite: limitItens };

      if (dadosFiltros) {
        setFiltroItens(dadosFiltros);
      }

      const params = getParamsFiltroItens(obj, dadosFiltros ?? filtroItens);

      try {
        const { status, data } = await msAw.get(
          `/veiculos/bloqueio/?${params}`,
        );

        setListaItensBloqueio(data.itens);
        setTotalRowsItens(data.meta.total);
        const response = new HttpResponse(false, status, data);
        setError(response);
        return response;
      } catch (error: IHttpError<IData, any> | any) {
        const response = new HttpError<IData>(true, error);
        setError(response);
        setListaItensBloqueio([]);
        setTotalRowsItens(0);
        return response;
      } finally {
        setLoadingItens(false);
      }
    },
    [pageItens, limitItens],
  );

  const relatorioItens = async () => {
    setLoadingItens(true);

    try {
      if (itensSelecionados.length) {
        const obj: any = {
          limite: 0,
          itens: JSON.stringify(
            itensSelecionados.map((item) => item.idItemBloqueioAdministrativo),
          ),
        };

        const params = getParamsFiltroItens(obj, filtroItens);
        const { status, data } = await msAw.get(
          `/veiculos/bloqueio/?${params}`,
        );

        const response = new HttpResponse(false, status, data);
        setError(response);
        return response;
      }

      const params = getParamsFiltroItens(
        { limite: LIMITE_EXPORTACAO },
        filtroItens,
      );
      const { status, data } = await msAw.get(`/veiculos/bloqueio/?${params}`);

      let limiteExcedido = false;

      if (data.itens.length === LIMITE_EXPORTACAO) {
        const paramsConfirmacao = getParamsFiltroItens(
          { limite: LIMITE_EXPORTACAO + 1 },
          filtroItens,
        );
        const { data: dataConfirmacao } = await msAw.get(
          `/veiculos/bloqueio/?${paramsConfirmacao}`,
        );

        limiteExcedido = dataConfirmacao.itens.length > LIMITE_EXPORTACAO;
      }

      const response = new HttpResponse(false, status, {
        ...data,
        limiteExcedido,
      });
      setError(response);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      return response;
    } finally {
      setLoadingItens(false);
    }
  };

  const buscarHistoricoVeiculo = async (idVeiculo: number) => {
    setLoadingItens(true);

    try {
      const { status, data } = await msAw.get(
        `/veiculos/bloqueio/historico/veiculo/${idVeiculo}`,
      );

      const response = new HttpResponse(false, status, data);
      setError(response);
      setHistoricoVeiculo(data);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      setHistoricoVeiculo([]);
      return response;
    } finally {
      setLoadingItens(false);
    }
  };

  const solicitacaoContraria = async (idsItens: number[], acao: string) => {
    setLoadingItens(true);
    setClearRowsListaItens(true);
    try {
      const { status, data } = await msAw.post(`/veiculos/bloqueio/reverter`, {
        idsItens,
        acao,
      });

      const response = new HttpResponse(false, status, data);
      setError(response);
      setItensSelecionados([]);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      return response;
    } finally {
      setLoadingItens(false);
      setClearRowsListaItens(false);
    }
  };

  const getParamsFiltro = (
    obj: any,
    dadosFiltros: IFiltroBloqueioAdministrativo,
  ) => {
    if (dadosFiltros.idEmpresa) {
      obj.idEmpresa = dadosFiltros.idEmpresa;
    }

    if (dadosFiltros.idsLotes) {
      obj.lotes = JSON.stringify(dadosFiltros.idsLotes);
    }

    if (dadosFiltros.dadosVeiculos) {
      obj.veiculos = JSON.stringify(dadosFiltros.dadosVeiculos);
    }

    if (dadosFiltros.cnpj) {
      obj.cnpj = limparCnpj(dadosFiltros.cnpj);
    }

    if (
      dadosFiltros.dataCriacaoLote &&
      dadosFiltros.dataCriacaoLote[0] &&
      dadosFiltros.dataCriacaoLote[1]
    ) {
      obj.dataInicio = new Data(dadosFiltros.dataCriacaoLote[0]).getDataEua();
      obj.dataFim = new Data(dadosFiltros.dataCriacaoLote[1]).getDataEua();
    }

    const params = Object.keys(obj)
      .map(
        (key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`,
      )
      .join("&");

    return params;
  };

  const listarLotes = useCallback(
    async (dadosFiltros?: IFiltroBloqueioAdministrativo) => {
      setLoading(true);
      const obj: any = { pagina: page, limite: limit };

      if (dadosFiltros) {
        setFiltro(dadosFiltros);
      }

      const params = getParamsFiltro(obj, dadosFiltros ?? filtro);

      try {
        const { status, data } = await msAw.get(
          `/veiculos/bloqueio/?${params}`,
        );

        setDadosLotesBloqueio(data.itens);
        setTotalRows(data.meta.total);
        const response = new HttpResponse(false, status, data);
        setError(response);
        return response;
      } catch (error: IHttpError<IData, any> | any) {
        const response = new HttpError<IData>(true, error);
        setError(response);
        setDadosLotesBloqueio([]);
        setTotalRows(0);
        return response;
      } finally {
        setLoading(false);
      }
    },
    [page, limit],
  );

  const relatorioLotes = async () => {
    setLoading(true);
    const obj: any = {
      limite: 0,
    };

    const params = getParamsFiltro(obj, filtro);

    try {
      const { status, data } = await msAw.get(`/veiculos/bloqueio/?${params}`);

      const response = new HttpResponse(false, status, data);
      setError(response);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      return response;
    } finally {
      setLoading(false);
    }
  };

  const listarDetalhesLote = useCallback(
    async (idLote: number) => {
      setLoadingDetalhes(true);

      try {
        let params =
          pesquisaItensLote.length > 3 ? `pesquisa=${pesquisaItensLote}` : "";

        const { status, data } = await msAw.get(
          `/veiculos/bloqueio/itens/lote/${idLote}?pagina=${pageDetalhes}&limite=${limitDetalhes}&${params}`,
        );

        setDadosLote(data.itens);
        setTotalRowsDetalhes(data.meta.total);
        const response = new HttpResponse(false, status, data);
        setError(response);
        return response;
      } catch (error: IHttpError<IData, any> | any) {
        const response = new HttpError<IData>(true, error);
        setError(response);
        setDadosLote([]);
        setTotalRowsDetalhes(0);
        return response;
      } finally {
        setLoadingDetalhes(false);
      }
    },
    [pageDetalhes, limitDetalhes, pesquisaItensLote],
  );

  const buscarResumoLote = async (idLote: number) => {
    setLoadingDetalhes(true);

    try {
      const { status, data } = await msAw.get(
        `/veiculos/bloqueio/resumo/lote/${idLote}/`,
      );

      setResumoLote(data);
      const response = new HttpResponse(false, status, data);
      setError(response);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      setResumoLote(defaultResumoLote);
      return response;
    } finally {
      setLoadingDetalhes(false);
    }
  };

  const reprocessarItensComErro = async (idLote: number | null) => {
    if (!idLote) {
      return;
    }
    setLoadingDetalhes(true);

    try {
      const { status, data } = await msAw.post(
        `/veiculos/bloqueio/reprocessar/lote/${idLote}`,
      );

      const response = new HttpResponse(false, status, data);
      setError(response);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      return response;
    } finally {
      setLoadingDetalhes(false);
    }
  };

  const validarProcessos = async (processos: number[]) => {
    setLoading(true);
    try {
      const { status, data } = await msAw.get(
        `/processo/validar/?processos=${JSON.stringify(processos)}`,
      );

      const response = new HttpResponse(false, status, data);
      setError(response);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      return response;
    } finally {
      setLoading(false);
    }
  };

  const validarLotes = async (lotes: number[]) => {
    setLoading(true);
    try {
      const { status, data } = await msAw.get(
        `/veiculos/bloqueio/validar/lotes/?lotes=${JSON.stringify(lotes)}`,
      );

      const response = new HttpResponse(false, status, data);
      setError(response);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      return response;
    } finally {
      setLoading(false);
    }
  };

  const validarVeiculos = async (placasChassisRenavams: string[]) => {
    setLoading(true);
    try {
      const { status, data } = await msAw.get(
        `/processo/veiculo/validar/?placasChassisRenavams=${JSON.stringify(
          placasChassisRenavams,
        )}`,
      );

      const response = new HttpResponse(false, status, data);
      setError(response);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      return response;
    } finally {
      setLoading(false);
    }
  };

  const onClickProcesso = async (id: number | string) => {
    setLoading(true);
    try {
      const { status, data } = await api.get(`/processos/param/${id}`);
      window.open(
        `${process.env.REACT_APP_DESPACHANTE_BASE_URL}/pages/processo/Processo.php?param=${data}`,
        "_blank",
      );
      const response = new HttpResponse(false, status, data);
      setError(response);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      return response;
    } finally {
      setLoading(false);
    }
  };

  const salvarLote = async (
    dadosCriarLote: IDadosCriarLote,
    dadosPlanilhaCriarLote: IPlanilhaCriarLote[],
  ) => {
    setLoading(true);
    try {
      const { status, data } = await msAw.post(`/veiculos/bloqueio/`, {
        tipo: dadosCriarLote.tipo,
        empresa: dadosCriarLote.empresa,
        divisao: dadosCriarLote.divisao,
        centroCusto: dadosCriarLote.centroCusto,
        solicitante: dadosCriarLote.solicitante,
        cnpj: dadosCriarLote.cnpj,
        veiculos: dadosPlanilhaCriarLote.map((dado) => {
          return {
            placa: dado.placa,
            chassi: dado.chassi,
            renavam: dado.renavam,
            uf: dado.uf,
          };
        }),
      });

      const response = new HttpResponse(false, status, data);
      setError(response);
      return response;
    } catch (error: IHttpError<IData, any> | any) {
      const response = new HttpError<IData>(true, error);
      setError(response);
      return response;
    } finally {
      setLoading(false);
    }
  };

  return {
    error,
    totalRows,
    page,
    limit,
    loading,
    setPage,
    setLimit,
    filtro,
    setFiltro,
    validarProcessos,
    validarLotes,
    validarVeiculos,
    listarLotes,
    dadosLotesBloqueio,
    relatorioLotes,
    totalRowsDetalhes,
    setTotalRowsDetalhes,
    loadingDetalhes,
    setLoadingDetalhes,
    pageDetalhes,
    setPageDetalhes,
    limitDetalhes,
    setLimitDetalhes,
    dadosLote,
    setDadosLote,
    listarDetalhesLote,
    resumoLote,
    setResumoLote,
    buscarResumoLote,
    pesquisaItensLote,
    setPesquisaItenslote,
    onClickProcesso,
    reprocessarItensComErro,
    salvarLote,
    listaItensBloqueio,
    setListaItensBloqueio,
    loadingItens,
    setLoadingItens,
    pageItens,
    setPageItens,
    limitItens,
    setLimitItens,
    filtroItens,
    setFiltroItens,
    totalRowsItens,
    setTotalRowsItens,
    listarItens,
    relatorioItens,
    buscarHistoricoVeiculo,
    historicoVeiculo,
    itensSelecionados,
    setItensSelecionados,
    clearRowsListaItens,
    solicitacaoContraria,
  };
}
